import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authService } from '@/services/authService'
import { organizationService } from '@/services/organizationService'
import type {
  OrgInvitation,
  OrgMembership,
  OrgPaginationParams,
  OrgRole,
  UserOrganization,
} from '@/types/organization'

/**
 * React Query layer for the organization APIs.
 *
 * The screens under `src/screens/organizations/` hand-roll `useEffect`/`useState`
 * and re-fetch *everything* after every mutation. These hooks exist so the
 * Organization tab can invalidate precisely instead.
 *
 * Query-key shape mirrors `useProfile.ts`: a root `all` tuple with narrower
 * factories beneath it, so a mutation can invalidate one slice
 * (e.g. `members(orgId)`) or a whole subtree by prefix.
 */
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  detail: (orgId: string) => [...organizationKeys.all, 'detail', orgId] as const,
  members: (orgId: string) => [...organizationKeys.all, 'members', orgId] as const,
  invitations: (orgId: string) => [...organizationKeys.all, 'invitations', orgId] as const,
  /** Prefix for every paginated page of one org's activity rollup. */
  inquirySummaries: (orgId: string) =>
    [...organizationKeys.all, 'inquiry-summary', orgId] as const,
  inquirySummary: (orgId: string, params: OrgPaginationParams) =>
    [...organizationKeys.inquirySummaries(orgId), params] as const,
  /** Prefix for every page of one member's inquiries. */
  memberInquiriesAll: (orgId: string, userId: string) =>
    [...organizationKeys.all, 'member-inquiries', orgId, userId] as const,
  memberInquiries: (orgId: string, userId: string, params: OrgPaginationParams) =>
    [...organizationKeys.memberInquiriesAll(orgId, userId), params] as const,
}

/**
 * Several of these endpoints answer 403 by design for non-managers, and
 * `apiClient` already retries internally. Retrying a deliberate 403 in React
 * Query on top of that just multiplies the noise.
 */
const NO_RETRY = { retry: false } as const

/**
 * Every organization the current user belongs to, each with *their own role*.
 *
 * `GET /organizations` returns `{role, organization}` pairs, so the caller's role
 * is available from this single call — see `useMyOrgRole`.
 */
export const useMyOrganizations = () => {
  const query = useQuery({
    queryKey: organizationKeys.lists(),
    queryFn: () => organizationService.listOrganizations(),
    staleTime: 5 * 60 * 1000,
    ...NO_RETRY,
  })

  return {
    organizations: (query.data ?? []) as UserOrganization[],
    isLoading: query.isLoading,
    error: toMessage(query.error),
    refetch: query.refetch,
  }
}

/**
 * The user's primary organization.
 *
 * The API models membership as many-to-many, but the product today assumes one
 * institution per user, so the Organization tab renders the first membership.
 * Returning the whole list alongside it keeps an org switcher cheap to add later.
 */
export const useMyPrimaryOrganization = () => {
  const { organizations, isLoading, error, refetch } = useMyOrganizations()
  const primary = organizations.length > 0 ? organizations[0] : null

  return {
    membership: primary,
    organization: primary?.organization ?? null,
    orgId: primary?.organization.id ?? '',
    isLoading,
    error,
    refetch,
  }
}

/** Full member list. Readable by *any* member of the org, not just managers. */
export const useOrgMembers = (orgId: string, enabled = true) => {
  const query = useQuery({
    queryKey: organizationKeys.members(orgId),
    queryFn: () => organizationService.listMembers(orgId),
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
    ...NO_RETRY,
  })

  return {
    members: (query.data ?? []) as OrgMembership[],
    isLoading: query.isLoading,
    error: toMessage(query.error),
  }
}

/**
 * The current user's role in `orgId`, plus the permission booleans derived from it.
 *
 * Preferred source is `GET /organizations`, which already carries the caller's
 * role — one call, and it works even when the members list has not been fetched.
 * We fall back to matching `authService.getUser()?.id` against the members list
 * (the precedent in `OrganizationDetailScreen`) for the case where the org came
 * from somewhere other than the list query.
 *
 * Permission booleans mirror the backend exactly:
 *  - canManage  (OWNER|ADMIN): invitations, member removal, activity/inquiries
 *  - canChangeRoles (OWNER only)
 *  - canInviteOwner (OWNER only — an ADMIN may not invite an OWNER)
 */
export const useMyOrgRole = (orgId: string) => {
  const { organizations, isLoading: orgsLoading } = useMyOrganizations()

  const roleFromList = useMemo(
    () => organizations.find((o) => o.organization.id === orgId)?.role,
    [organizations, orgId],
  )

  // Only pay for the members call when the list query could not answer.
  const needsFallback = !!orgId && !orgsLoading && !roleFromList
  const { members, isLoading: membersLoading } = useOrgMembers(orgId, needsFallback)

  const roleFromMembers = useMemo(() => {
    if (!needsFallback) return undefined
    const currentUserId = authService.getUser()?.id
    if (!currentUserId) return undefined
    return members.find((m) => m.user_id === currentUserId)?.role
  }, [needsFallback, members])

  const role: OrgRole | undefined = roleFromList ?? roleFromMembers
  const isLoading = orgsLoading || (needsFallback && membersLoading)

  return {
    role,
    isLoading,
    isOwner: role === 'OWNER',
    isAdmin: role === 'ADMIN',
    canManage: role === 'OWNER' || role === 'ADMIN',
    canChangeRoles: role === 'OWNER',
    canInviteOwner: role === 'OWNER',
  }
}

/** Pending/settled invitations. OWNER/ADMIN only — gate with `enabled`. */
export const useOrgInvitations = (orgId: string, enabled = true) => {
  const query = useQuery({
    queryKey: organizationKeys.invitations(orgId),
    queryFn: () => organizationService.listInvitations(orgId),
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
    ...NO_RETRY,
  })

  return {
    invitations: (query.data ?? []) as OrgInvitation[],
    isLoading: query.isLoading,
    error: toMessage(query.error),
  }
}

/**
 * Members with their bid/RFQ counts. OWNER/ADMIN only — gate with `enabled`.
 * One request for the whole list; do not call `useOrgMemberInquiries` per row.
 */
export const useOrgInquirySummary = (
  orgId: string,
  params: OrgPaginationParams,
  enabled = true,
) => {
  const query = useQuery({
    queryKey: organizationKeys.inquirySummary(orgId, params),
    queryFn: () => organizationService.getInquirySummary(orgId, params),
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
    ...NO_RETRY,
  })

  return {
    summary: query.data ?? null,
    isLoading: query.isLoading,
    error: toMessage(query.error),
  }
}

/** One member's bids + RFQs. OWNER/ADMIN only. */
export const useOrgMemberInquiries = (
  orgId: string,
  userId: string | null,
  params: OrgPaginationParams,
  enabled = true,
) => {
  const query = useQuery({
    queryKey: organizationKeys.memberInquiries(orgId, userId ?? '', params),
    queryFn: () => organizationService.getMemberInquiries(orgId, userId as string, params),
    enabled: !!orgId && !!userId && enabled,
    staleTime: 60 * 1000,
    ...NO_RETRY,
  })

  return {
    inquiries: query.data ?? null,
    isLoading: query.isLoading,
    error: toMessage(query.error),
  }
}

export const useInviteMember = (orgId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: OrgRole }) =>
      organizationService.createInvitation(orgId, email, role),
    onSuccess: () => {
      // Only the invitation list changes — membership does not exist until accept.
      queryClient.invalidateQueries({ queryKey: organizationKeys.invitations(orgId) })
    },
  })
}

export const useRevokeInvitation = (orgId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (inviteId: string) => organizationService.revokeInvitation(orgId, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.invitations(orgId) })
    },
  })
}

export const useUpdateMemberRole = (orgId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      organizationService.updateMemberRole(orgId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(orgId) })
      // Rows in the activity rollup carry `role`, so they are stale too.
      queryClient.invalidateQueries({ queryKey: organizationKeys.inquirySummaries(orgId) })
      // The caller may have just changed their own role.
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() })
    },
  })
}

export const useRemoveMember = (orgId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => organizationService.removeMember(orgId, userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(orgId) })
      queryClient.invalidateQueries({ queryKey: organizationKeys.inquirySummaries(orgId) })
      // That member's inquiries are no longer readable through this org.
      queryClient.removeQueries({
        queryKey: organizationKeys.memberInquiriesAll(orgId, userId),
      })
    },
  })
}

const toMessage = (error: unknown): string | null => {
  if (!error) return null
  return error instanceof Error ? error.message : String(error)
}
